import { useEffect, useState } from 'react';
import { sanitizeValue } from '../lib/sanitize';
import { HosApiInventoryDevice } from '../types/apiTypes';
import { useFetch } from './useFetch';
import { useOperator } from '../contexts/OperatorContext';
import { useNotification } from '../contexts/NotificationContext';
import { BASE_URL_MAP } from '../lib/environment';

type Action =
  | ''
  | 'addInventory'
  | 'addBulkInventory'
  | 'deleteInventory'
  | 'getInventoryDevice'
  | 'getInventory'
  | 'updateDeviceMAC'
  | 'updateDeviceSerial'
  | 'updateDeviceStatus'
  | 'updateDeviceOperator';

export type FailedImport = {
  reason: string;
  device: InventoryDevice;
};

export const useInventory = (disableNotifications: boolean) => {
  const [inventory, setInventory] = useState<InventoryDevice[]>([]);
  const [inventoryDevice, setInventoryDevice] =
    useState<InventoryDevice | null>(null);
  const [deletedDeviceId, setDeletedDeviceId] = useState<string | null>(null);
  const [action, setAction] = useState<Action>('');
  const [failedImports, setFailedImports] = useState<
    FailedImport[] | undefined
  >();
  const notification = useNotification();
  const operator = useOperator();
  const { error, loading, makeRequest, response } =
    useFetch(disableNotifications);

  useEffect(() => {
    resetState();
  }, [operator.id]);

  useEffect(() => {
    if (response && !loading) {
      handleActionResponse(response);
    } else if (!loading && !error && action === 'deleteInventory') {
      setInventory((inventory) =>
        inventory.filter((i) => i.mac !== deletedDeviceId)
      );
      setDeletedDeviceId(null);
      setAction('');
    }
    //eslint-disable-next-line
  }, [response, loading]);

  const handleActionResponse = (response: any) => {
    if (action === 'addInventory') handleAddInventoryResponse(response);
    if (action === 'addBulkInventory') BulkAddInventoryResponse(response);
    else if (action === 'getInventoryDevice') {
      const mappedDeviceResponse = mapInventoryDevice(response, operator);
      setInventoryDevice(mappedDeviceResponse);
    } else if (action === 'getInventory') {
      setInventory(
        response.map((device: HosApiInventoryDevice | ApiResInventoryDevice) =>
          mapInventoryDevice(device, operator)
        )
      );
    } else if (action === 'updateDeviceOperator') {
      const mappedDeviceResponse = mapInventoryDevice(response, operator);
      setInventory((inventory) =>
        inventory.filter((i) => i.id !== mappedDeviceResponse.id)
      );
    } else if (action.includes('update')) {
      const mappedDeviceResponse = mapInventoryDevice(response, operator);
      const matchedIndex = inventory.findIndex(
        (i) => i.id === mappedDeviceResponse.id
      );
      const updatedInventory = inventory.slice();
      updatedInventory.splice(matchedIndex, 1, mappedDeviceResponse);

      setInventory(updatedInventory);
    }
  };

  const addBulkInventory = (devices: AddInventoryDevice[]) => {
    setAction('addBulkInventory');
    addDeviceInventory(devices);
  };

  const addInventory = (devices: AddInventoryDevice[]) => {
    setAction('addInventory');
    addDeviceInventory(devices);
  };

  const addDeviceInventory = (devices: AddInventoryDevice[]) => {
    const mappedDevices = mapDevicesForApi(
      devices,
      operator.type === 'Residential'
    );
    makeRequest(
      `${BASE_URL_MAP[operator.type]}/${operator?.id}/Inventory${
        operator.type === 'Residential' ? '/bulk' : ''
      }`,
      'post',
      sanitizeValue(mappedDevices)
    );
  };

  const deleteInventory = (deviceId: string) => {
    setDeletedDeviceId(null);
    setAction('deleteInventory');
    if (deviceId && operator.type === 'Residential') {
      setDeletedDeviceId(deviceId);
      makeRequest(
        `${BASE_URL_MAP['Residential']}/${operator?.id}/Inventory/${deviceId}`,
        'delete'
      );
    }
  };

  const getInventory = (searchFor?: string, numberOfResults?: number) => {
    setAction('getInventory');
    if (searchFor && numberOfResults !== undefined)
      makeRequest(
        `${BASE_URL_MAP[operator.type]}/${
          operator?.id
        }/Inventory/Search/${searchFor}/${numberOfResults}`,
        'get'
      );
    else
      makeRequest(
        `${BASE_URL_MAP[operator.type]}/${operator?.id}/Inventory`,
        'get'
      );
  };

  const getInventoryDevice = (deviceId: string) => {
    setAction('getInventoryDevice');
    if (deviceId)
      makeRequest(
        `${BASE_URL_MAP[operator.type]}/${operator?.id}/Inventory/${deviceId}`,
        'get'
      );
  };

  const updateDeviceMAC = (deviceId: string, macAddress: string) => {
    setAction('updateDeviceMAC');
    if (deviceId && macAddress)
      makeRequest(
        `${BASE_URL_MAP['Hospitality']}/${
          operator?.id
        }/Inventory/${deviceId}/UpdateMACAddress/${sanitizeValue(macAddress)}`,
        'put'
      );
  };

  const updateDeviceSerial = (deviceId: string, serial: string) => {
    setAction('updateDeviceSerial');
    if (deviceId && serial)
      makeRequest(
        `${BASE_URL_MAP['Hospitality']}/${
          operator?.id
        }/Inventory/${deviceId}/UpdateSerial/${sanitizeValue(serial)}`,
        'put'
      );
  };

  const updateDeviceOperator = (deviceId: string, newOperatorId: number) => {
    setAction('updateDeviceOperator');
    if (deviceId && newOperatorId)
      makeRequest(
        `${BASE_URL_MAP[operator.type]}/${
          operator?.id
        }/Inventory/${deviceId}/UpdateOperator/${newOperatorId}`,
        'put'
      );
  };

  const updateDeviceStatus = (deviceId: string, statusId: number) => {
    setAction('updateDeviceStatus');
    if (deviceId && statusId)
      makeRequest(
        `${BASE_URL_MAP[operator.type]}/${
          operator?.id
        }/Inventory/${deviceId}/UpdateStatus/${statusId}`,
        'put'
      );
  };

  const addToastNotification = (name: string, message: string) => {
    notification.addNotification({
      dismissible: true,
      message: message,
      name: name,
      type: 'error',
    });
  };

  const handleAddInventoryResponse = (response: any) => {
    if (operator.type === 'Residential')
      resAddInventoryResponse(response as ApiResAddInventoryResponse);
    else hosAddInventoryResponse(response as ApiHosAddInventoryResponse);
  };

  const hosAddInventoryResponse = (response: ApiHosAddInventoryResponse) => {
    if (response.Success.length)
      setInventory((inventory) => [
        ...response.Success.map((device) =>
          mapInventoryDevice(device, operator)
        ),
        ...inventory,
      ]);
    if (response.Fail.length) {
      if (response.Fail.length < 5)
        response.Fail.forEach((error) => {
          addToastNotification(
            'Failed to Add Device',
            `${error.Reason}: ${error.Device.ID}`
          );
        });
      else
        addToastNotification(
          'Failed to Add Devices',
          `Failed to add ${response.Fail.length} devices.`
        );
    }
  };

  const BulkAddInventoryResponse = (response: ResBulkInventoryResponse) => {
    if (response.Success.length) {
      setInventory((inventory) => [
        ...response.Success.map((device) =>
          mapInventoryDevice(device, operator)
        ),
        ...inventory,
      ]);
    }
    if (response.Fail.length) {
      setFailedImports(() =>
        response.Fail.map((error) => ({
          device: mapInventoryDevice(error.Device, operator),
          reason: error.Reason,
        }))
      );
    }
  };

  const resAddInventoryResponse = (response: ApiResAddInventoryResponse) => {
    if (response.Success.length)
      setInventory((inventory) => [
        ...response.Success.map((device) =>
          mapInventoryDevice(device, operator)
        ),
        ...inventory,
      ]);
    if (response.Fail.length) {
      if (response.Fail.length < 5)
        response.Fail.forEach((error) => {
          const device = error.Device.DeviceID
            ? `Device ID: ${error.Device.DeviceID}`
            : `MAC: ${error.Device.MACAddress}`;

          addToastNotification('Failed to Add Device', device);
        });
      else
        addToastNotification(
          'Failed to Add Devices',
          `Failed to add ${response.Fail.length} devices.`
        );
    }
  };

  const updateInventoryState = (deviceId: string, action: 'remove' | 'add') => {
    if (action === 'remove')
      setInventory((inventory) => inventory.filter((d) => d.id !== deviceId));
    else {
      // Waiting 3 seconds for animation in useAccountActions
      setTimeout(getInventory, 3000);
      // TODO: Remove call to getInventory and efficiently add the device back into memory when state is re-architected
      // const device = inventory.find((d) => d.id === deviceId);
      // if (device) setInventory((inventory) => [device, ...inventory]);
    }
  };

  const clearImportFailures = () => setFailedImports(undefined);

  const resetState = () => {
    setInventory([]);
    setAction('');
    setDeletedDeviceId(null);
  };

  return {
    addInventory,
    addBulkInventory,
    clearImportFailures,
    deleteInventory,
    failedImports,
    loading: loading && action !== 'addBulkInventory',
    response,
    importLoading: loading && action === 'addBulkInventory',
    inventory,
    inventoryDevice,
    getInventory,
    getInventoryDevice,
    resetState,
    updateDeviceMAC,
    updateDeviceSerial,
    updateDeviceOperator,
    updateDeviceStatus,
    updateInventoryState,
  };
};

const mapHosDeviceForApi = (
  device: AddInventoryDevice
): ApiHosAddInventory => ({
  ID: device.id,
  MACAddress: device.mac,
  ModelID: device.model,
  OrderDate: device.orderDate ? device.orderDate.toISOString() : undefined,
  SerialNumber: device.serial,
  StatusID: 1,
});

const mapResDeviceForApi = (
  device: AddInventoryDevice
): ApiResAddInventory => ({
  // @ts-ignore
  DeviceID: device.id,
  MACAddress: device.mac,
  Model: device.model,
  SerialNumber: device.serial,
});

const mapDevicesForApi = (
  devices: AddInventoryDevice[],
  isRes: boolean
): ApiHosAddInventory[] | ApiResAddInventory[] => {
  if (isRes) return devices.map((d) => mapResDeviceForApi(d));
  return devices.map((d) => mapHosDeviceForApi(d));
};

const mapHosInventoryDevice = (
  device: HosApiInventoryDevice,
  operator: OperatorContext
): InventoryDevice => {
  let orderDate = device.OrderDate ? new Date(device.OrderDate) : undefined;
  if (orderDate && orderDate < new Date('2000-01-01')) {
    orderDate = undefined;
  }
  const deviceModel = operator.models.find(
    (model: DeviceModel) => model.id === device.ModelID
  );

  return {
    id: device.ID,
    mac: device.MACAddress,
    serial: device.SerialNumber,
    modelId: device.ModelID,
    modelName: deviceModel?.name ?? '',
    deviceType:
      typeof deviceModel?.type === 'object'
        ? deviceModel.type?.name
        : deviceModel?.type,
    status: device.StatusID,
    orderDate: orderDate,
  };
};

const mapResInventoryDevice = (
  device: ApiResInventoryDevice,
  operator: OperatorContext
): InventoryDevice => {
  const model = operator.models?.find(
    (model: DeviceModel) =>
      model.name === (device.DeviceModel?.Model ?? device.Model)
  );

  return {
    id: device?.DeviceID,
    mac: device?.MACAddress,
    serial: device?.SerialNumber,
    modelId: model?.name ?? '',
    modelName: model?.name ?? '',
  };
};

const mapInventoryDevice = (
  device: HosApiInventoryDevice | ApiResInventoryDevice,
  operator: OperatorContext
) => {
  if (operator.type === 'Residential')
    return mapResInventoryDevice(device as ApiResInventoryDevice, operator);
  return mapHosInventoryDevice(device as HosApiInventoryDevice, operator);
};
